import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSuperAdminOverview } from "../../service/api";
import {
  Card, Kpi, Delta, Pill, PeriodPicker, PRESETS, StatusBars, Funnel, Legend, MiniBar, useSort,
  fmtNum, fmtMoney, fmtMoneyObj, fmtPct, fmtDate, healthPill, severityPill, isSuperAdmin, Forbidden, Loading
} from "./ui";
import { CompanyStackedBar, TrendLine, MoneyTrend } from "./charts";

export default function IntelligenceOverview() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState(PRESETS[0].range());
  const [customerId, setCustomerId] = useState("");
  const [skillFamily, setSkillFamily] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getSuperAdminOverview({ ...range, customerId, skillFamily })
      .then((res) => { setData(res?.data || null); setUpdatedAt(new Date()); })
      .catch((err) => { console.error(err); toast.error(err?.response?.data?.message || "Could not load the dashboard"); })
      .finally(() => setLoading(false));
  }, [range, customerId, skillFamily]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin()) return <Forbidden />;

  const k = data?.kpis || {};
  const p = data?.period || {};
  const companies = data?.companies || [];
  const mainCurrency = (k.revenue?.USD || 0) > (k.revenue?.INR || 0) ? "USD" : "INR";

  return (
    <div className="intel-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 className="intel-title">Business Intelligence <Pill tone="solid">Super admin</Pill></h1>
          <p className="intel-subtitle">How many interviews we run, where they come from, what they earn and where to act. Numbers use each interview's scheduled date.</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="intel-btn secondary" type="button" onClick={() => navigate("/admin/intelligence/admins")}>Admin team performance</button>
          <button className="intel-btn secondary" type="button" onClick={() => navigate("/admin/intelligence/salaries")}>Salary settings</button>
        </div>
      </div>

      <div className="intel-toolbar">
        <PeriodPicker preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
        <select className="intel-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">All companies</option>
          {(data?.customers || []).map((c) => <option key={c.customerId} value={c.customerId}>{c.companyName}</option>)}
        </select>
        <select className="intel-select" value={skillFamily} onChange={(e) => setSkillFamily(e.target.value)}>
          <option value="">All skill families</option>
          {(data?.skillFamilies || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="spacer" />
        <span className="intel-updated">{p.startDate ? `${fmtDate(p.startDate)} – ${fmtDate(p.endDate)} · compared with the ${p.days} days before` : ""}{updatedAt ? ` · updated ${updatedAt.toLocaleTimeString()}` : ""}</span>
        <button className="intel-btn secondary small" type="button" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {loading && !data ? <Loading /> : !data ? <div className="intel-card"><div className="intel-empty">No data.</div></div> : (
        <>
          {/* KPIs */}
          <div className="intel-grid cols-6">
            <Kpi label="Interviews" value={fmtNum(k.interviews)} help="All interview schedules whose slot date falls in the period."
              sub={<Delta value={k.interviewsChangePct} />} />
            <Kpi label="Completed" value={fmtNum(k.completed)} help="Schedules with status COMPLETED." sub={`${fmtPct(k.completionRate)} completion rate`} />
            <Kpi label="Upcoming" value={fmtNum(k.upcoming)} help="Scheduled interviews from today onward (not limited to the period)." sub={`${fmtNum(k.next48h)} in the next 48 hours`} />
            <Kpi label="Cancel + no-show rate" value={fmtPct(k.cancelLikeRate)} valueTone={k.cancelLikeRate >= 10 ? "bad" : ""} border={k.cancelLikeRate >= 15 ? "bad" : k.cancelLikeRate >= 10 ? "warn" : ""}
              help="Cancelled, candidate no-show and panel no-show as a share of all interviews in the period."
              sub={<Delta value={Math.round((k.cancelLikeRate - k.priorCancelLikeRate) * 10) / 10} higherIsBetter={false} pts />} />
            <Kpi label="Revenue billed" value={fmtMoneyObj(k.revenue)} help="Price per interview from each customer's rate card (on the customer's ticket), by status. Scheduled interviews are not billed yet."
              sub={`Panel cost ${fmtMoneyObj(k.panelCost)}`} />
            <Kpi label="Gross margin" value={fmtMoneyObj(k.margin)} valueTone={(k.margin?.[mainCurrency] || 0) < 0 ? "bad" : "good"} help="Revenue billed minus panel cost."
              sub={<span>{fmtPct(k.marginPct)} margin · <Delta value={Math.round((k.marginPct - k.priorMarginPct) * 10) / 10} pts suffix="" /></span>} />
          </div>

          {/* Alerts */}
          <Card title="Needs attention" hint="Rule-based checks on this period's data. No AI is used." superOnly>
            <div className="intel-card-body">
              {!data.alerts?.length ? <div className="intel-empty" style={{ padding: 12 }}>Nothing needs attention for this period.</div> : (
                <div className="alert-list">
                  {data.alerts.map((a, i) => (
                    <div className="alert-item" key={i}>
                      {severityPill(a.severity)}
                      <div className="body">
                        <div className="title">{a.title}</div>
                        <div className="muted">{a.detail}{a.customerId && <> · <a href={`/admin/intelligence/company/${a.customerId}`} onClick={(e) => { e.preventDefault(); navigate(`/admin/intelligence/company/${a.customerId}`); }}>Open company</a></>}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Company chart + status mix */}
          <div className="intel-grid two-one">
            <Card title="Interviews by company" hint={`Top ${Math.min(10, companies.length)} of ${companies.length} · click a bar to open the company`}>
              <div className="intel-card-body">
                <div style={{ marginBottom: 10 }}><Legend keys={["completed", "scheduled", "candidateNoShow", "panelNoShow", "cancelled", "rescheduled"]} /></div>
                {companies.length ? <CompanyStackedBar companies={[...companies].sort((a, b) => b.interviews - a.interviews)} onClick={(c) => navigate(`/admin/intelligence/company/${c.customerId}`)} /> : <div className="intel-empty">No interviews in this period.</div>}
              </div>
            </Card>
            <Card title="Status mix" hint={`${fmtNum(data.statusMix?.total)} interviews`}>
              <div className="intel-card-body">
                <StatusBars mix={data.statusMix} />
                <div style={{ borderTop: "1px solid #E8E8E8", marginTop: 14, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  <div className="bar-row"><span className="muted">Feedback provided</span><span className="strong">{fmtNum(data.statusMix?.feedbackProvided)} of {fmtNum(data.statusMix?.completed)}</span></div>
                  <div className="bar-row"><span className="muted">Waiting for feedback over 48 h</span><span className={`strong ${k.pendingFeedback ? "warn" : ""}`}>{fmtNum(k.pendingFeedback)}</span></div>
                  <div className="bar-row"><span className="muted">Active companies</span><span className="strong">{fmtNum(k.activeCompanies)} · {fmtNum(k.newCompanies)} new</span></div>
                </div>
              </div>
            </Card>
          </div>

          {/* Trend + funnel */}
          <div className="intel-grid two-one">
            <Card title="Interview volume, last 6 months" hint="By scheduled month">
              <div className="intel-card-body"><TrendLine trend={data.trend} /></div>
            </Card>
            <Card title="Hiring funnel" hint="Candidates created in the period">
              <div className="intel-card-body"><Funnel funnel={data.funnel} /></div>
            </Card>
          </div>

          <Card title="Revenue and panel cost, last 6 months" hint={mainCurrency === "USD" ? "US-billed interviews (USD)" : "India-billed interviews (INR)"} superOnly>
            <div className="intel-card-body"><MoneyTrend trend={data.trend} currency={mainCurrency} /></div>
          </Card>

          <CompanyTable companies={companies} navigate={navigate} />
        </>
      )}
    </div>
  );
}

function CompanyTable({ companies, navigate }) {
  const { sorted, Th } = useSort(companies, "interviews");
  const healthRank = { "AT RISK": 0, WATCH: 1, HEALTHY: 2 };
  const rows = [...sorted].sort((a, b) => healthRank[a.health] - healthRank[b.health] || 0);
  return (
    <Card title="Companies: volume, pricing, margin and health" hint="Price = billed per interview · Panel cost = paid to panels · click a row for details" superOnly>
      <div className="intel-scroll">
        <table className="intel-table">
          <thead>
            <tr>
              <Th k="companyName">Company</Th>
              <Th k="interviews" num>Interviews</Th>
              <Th k="changePct" num>vs prior</Th>
              <Th k="completed" num>Completed</Th>
              <Th k="cancelLikeRate" num>Cancel / no-show</Th>
              <Th k="pendingFeedback" num>Pending feedback</Th>
              <Th k="openJobs" num>Open jobs</Th>
              <Th k="avgPrice" num>Avg price</Th>
              <Th k="avgPanelCost" num>Avg panel cost</Th>
              <th className="num">Revenue</th>
              <th className="num">Margin</th>
              <Th k="marginPct" num>Margin %</Th>
              <th>Health</th>
              <th>Next action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.customerId} className={`clickable ${c.health === "AT RISK" ? "" : ""}`} onClick={() => navigate(`/admin/intelligence/company/${c.customerId}`)}>
                <td className="strong">{c.companyName}{!c.priced && c.interviews > 0 && <> <Pill tone="amber">No rate card</Pill></>}</td>
                <td className="num">{fmtNum(c.interviews)}</td>
                <td className="num"><Delta value={c.changePct} suffix="" /></td>
                <td className="num">{fmtNum(c.completed)}</td>
                <td className={`num ${c.cancelLikeRate >= 15 ? "bad strong" : c.cancelLikeRate >= 10 ? "warn strong" : ""}`}>{fmtNum(c.cancelLike)} · {fmtPct(c.cancelLikeRate, 0)}</td>
                <td className={`num ${c.pendingFeedback >= 5 ? "warn strong" : ""}`}>{fmtNum(c.pendingFeedback)}</td>
                <td className="num">{fmtNum(c.openJobs)}</td>
                <td className="num">{c.avgPrice ? fmtMoney(c.avgPrice, c.currency, false) : "–"}</td>
                <td className="num">{c.avgPanelCost ? fmtMoney(c.avgPanelCost, c.currency, false) : "–"}</td>
                <td className="num">{c.priced ? fmtMoneyObj(c.revenue) : <span className="muted">no rate card</span>}</td>
                <td className={`num strong ${(c.margin?.[c.currency] || 0) < 0 ? "bad" : ""}`}>{c.priced ? fmtMoneyObj(c.margin) : "–"}</td>
                <td className="num"><MiniBar pct={c.marginPct} color={c.marginPct < 20 ? "#C0392B" : c.marginPct < 30 ? "#D9822B" : "#2E8B57"} /><span className={`strong ${c.marginPct < 20 ? "bad" : ""}`}>{c.priced ? fmtPct(c.marginPct, 0) : "–"}</span></td>
                <td>{healthPill(c.health)}</td>
                <td className={c.nextAction === "No action needed" ? "muted" : "strong"} style={{ color: c.nextAction === "No action needed" ? undefined : "#3E67B1" }}>{c.nextAction}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={14} className="intel-empty">No companies with interviews in this period.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
